import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  FileUploaderItem,
  ProgressBar,
} from '@carbon/react';
import { TrashCan, Upload as UploadIcon } from '@carbon/react/icons';

import {
  uploadHolidayMediaFile,
  useCreateHolidayMediaMutation,
  useLazyGetHolidayMediaQuery,
  useDeleteHolidayMediaMutation,
} from '@/redux/new_services/holidayMedia';

// HolidayMediaSlot encapsulates one landscape-or-portrait media slot of a
// HolidayVariant. It can either render a preview of an already-uploaded
// media or expose a file picker that runs the two-step upload flow + polls
// the conversion status until the row is "ready".
//
// Props:
//   - mediaId: number | null    — currently linked HolidayMedia id
//   - orientation: 'landscape' | 'portrait' — used as a label only
//   - onChange: (newMediaId: number | null) => void  — fired whenever the
//     slot's media id changes (after upload or after delete).
const HolidayMediaSlot = ({ mediaId, orientation, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploadPct, setUploadPct] = useState(null);     // 0-100 during upload
  const [processing, setProcessing] = useState(false);  // polling backend
  const [errorMsg, setErrorMsg] = useState(null);
  const [media, setMedia] = useState(null);             // loaded media record

  const [createHolidayMedia] = useCreateHolidayMediaMutation();
  const [deleteHolidayMedia] = useDeleteHolidayMediaMutation();
  const [loadMedia] = useLazyGetHolidayMediaQuery();

  // Load the current media record whenever the parent passes a new mediaId.
  useEffect(() => {
    if (!mediaId) {
      setMedia(null);
      return;
    }
    loadMedia(mediaId).unwrap().then(setMedia).catch(() => setMedia(null));
  }, [mediaId, loadMedia]);

  // Poll the show endpoint while the row is in "processing" so the UI flips
  // to a preview as soon as the worker is done. Stops on ready/failed.
  useEffect(() => {
    if (!media || media.status === 'ready' || media.status === 'failed') {
      setProcessing(false);
      return;
    }
    setProcessing(true);
    const handle = setInterval(() => {
      loadMedia(media.model.ID).unwrap().then((fresh) => {
        setMedia(fresh);
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(handle);
  }, [media, loadMedia]);

  // Read width/height from the file before we send it; the backend needs
  // them to drive the conversion pipeline.
  const probeDimensions = (file) =>
    new Promise((resolve) => {
      if (file.type.startsWith('video/')) {
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.onloadedmetadata = () => resolve({ width: v.videoWidth, height: v.videoHeight });
        v.onerror = () => resolve({ width: 0, height: 0 });
        v.src = URL.createObjectURL(file);
      } else {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve({ width: 0, height: 0 });
        img.src = URL.createObjectURL(file);
      }
    });

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErrorMsg(null);

    try {
      const { width, height } = await probeDimensions(file);
      // Step 1: raw upload → temp filename
      const uploaded = await uploadHolidayMediaFile(file, setUploadPct);
      // Step 2: register the row
      const created = await createHolidayMedia({
        name: file.name,
        filename: uploaded.filename,
        real_filename: uploaded.real_filename,
        content_type: uploaded.content_type,
        width,
        height,
      }).unwrap();
      const newId = created.data.model.ID;
      setMedia(created.data);
      setUploadPct(null);
      onChange?.(newId);
    } catch (err) {
      console.error('upload holiday media failed:', err);
      setErrorMsg('Upload fehlgeschlagen');
      setUploadPct(null);
    } finally {
      // Reset input so re-uploading the same filename triggers onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!mediaId) return;
    try {
      await deleteHolidayMedia(mediaId).unwrap();
    } catch (err) {
      // Even on backend error we drop the local reference so the variant
      // isn't stuck pointing at something the admin wanted to remove.
      console.warn('delete holiday media:', err);
    }
    setMedia(null);
    onChange?.(null);
  };

  const label = orientation === 'portrait' ? 'Hochformat' : 'Querformat';

  // Empty state — show file picker.
  if (!mediaId && !uploadPct) {
    return (
      <div className="holiday-media-slot holiday-media-slot--empty">
        <div className="holiday-media-slot__label">{label}</div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          kind="tertiary"
          renderIcon={UploadIcon}
          onClick={() => fileInputRef.current?.click()}
        >
          Datei auswählen
        </Button>
        {errorMsg && <div className="holiday-media-slot__error">{errorMsg}</div>}
      </div>
    );
  }

  // Uploading — progress bar.
  if (uploadPct !== null) {
    return (
      <div className="holiday-media-slot holiday-media-slot--uploading">
        <div className="holiday-media-slot__label">{label}</div>
        <ProgressBar
          label="Hochladen"
          helperText={`${uploadPct} %`}
          value={uploadPct}
          max={100}
        />
      </div>
    );
  }

  // Processing — polling until ready.
  if (media && media.status !== 'ready') {
    return (
      <div className="holiday-media-slot holiday-media-slot--processing">
        <div className="holiday-media-slot__label">{label}</div>
        <FileUploaderItem
          name={media.name}
          status={media.status === 'failed' ? 'edit' : 'uploading'}
          iconDescription={media.status === 'failed' ? 'Fehler' : 'Wird verarbeitet'}
        />
        {media.status === 'failed' && (
          <Button kind="ghost" size="sm" renderIcon={TrashCan} onClick={handleDelete}>
            Entfernen
          </Button>
        )}
      </div>
    );
  }

  // Ready — preview + delete.
  return (
    <div className="holiday-media-slot holiday-media-slot--ready">
      <div className="holiday-media-slot__label">{label}</div>
      {media?.type === 1 ? (
        <video
          className="holiday-media-slot__preview"
          src={media.s3_med_url || media.s3_url}
          muted
          loop
          autoPlay
          playsInline
        />
      ) : (
        <img
          className="holiday-media-slot__preview"
          src={media?.s3_thumb_url || media?.s3_url}
          alt={media?.name}
        />
      )}
      <Button
        kind="ghost"
        size="sm"
        renderIcon={TrashCan}
        onClick={handleDelete}
      >
        Entfernen
      </Button>
    </div>
  );
};

export default HolidayMediaSlot;
